import {LitElement, html, unsafeCSS} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {property} from 'lit/decorators/property.js';
import {state} from 'lit/decorators/state.js';
import styles from "./bg-background-video-control.scss?inline"

/**
 * Background Video Control Component
 * 
 * Provides play/pause controls for background videos with automatic scroll-based playback.
 * Auto-detects parent section and video type (YouTube/Vimeo).
 * 
 * @slot play-icon - Icon element to show when video is paused (will be shown when user can play)
 * @slot pause-icon - Icon element to show when video is playing (will be shown when user can pause)
 * 
 * @example
 * <bg-background-video-control>
 *   <i slot="play-icon" class="icon-play_circle_filled"></i>
 *   <i slot="pause-icon" class="icon-pause_circle_filled"></i>
 * </bg-background-video-control>
 */
@customElement('bg-background-video-control')
export class BgBackgroundVideoControl extends LitElement {

  @property({ type: String })
  observeTarget = '';

  @state()
  private isPlaying = true;

  @state()
  private userPaused = false;

  @state()
  private iframeLoaded = false;

  private videoType: string | null = null;
  private iframe: HTMLIFrameElement | null = null;
  private observer: IntersectionObserver | null = null;
  private playIcon: HTMLElement | null = null;
  private pauseIcon: HTMLElement | null = null;
  private isInView = false;

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    // Initialize after the component is connected to DOM
    setTimeout(() => this.initialize(), 0);
  }

  firstUpdated() {
    // Get references to slotted elements
    this.setupSlottedElements();
    this.updateIconVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Clean up observer when component is removed
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  private initialize() {
    // Auto-detect parent section and iframe
    const videoWrapper = this.previousElementSibling;
    if (!videoWrapper) {
      console.warn('No video wrapper found');
      return;
    }

    this.iframe = videoWrapper.querySelector('iframe');
    if (!this.iframe) {
      console.warn('No video iframe found');
      return;
    }

    this.videoType = this.getVideoType(this.iframe);
    if (!this.videoType) {
      return;
    }

    // For YouTube, ensure enablejsapi=1 is in the src
    if (this.videoType === 'youtube' && !this.iframe.src.includes('enablejsapi=1')) {
      console.warn('YouTube iframe missing enablejsapi=1 parameter');
      // Try to add it
      const separator = this.iframe.src.includes('?') ? '&' : '?';
      this.iframe.src += `${separator}enablejsapi=1`;
    }

    // Add iframe load event listener and API event listeners
    this.setupIframeLoadListener();

    // Set up intersection observer for parent section immediately
    this.setupIntersectionObserver(videoWrapper);
  }

  private setupSlottedElements() {
    // Get references to slotted play and pause icons
    this.playIcon = this.querySelector('[slot="play-icon"]');
    this.pauseIcon = this.querySelector('[slot="pause-icon"]');
  }

  private updateIconVisibility() {
    if (this.playIcon && this.pauseIcon) {
      if (this.isPlaying) {
        this.playIcon.style.display = 'none';
        this.pauseIcon.style.display = 'inline';
      } else {
        this.playIcon.style.display = 'inline';
        this.pauseIcon.style.display = 'none';
      }
    }
  }

  private getVideoType(iframe: HTMLIFrameElement): string | null {
    const src = iframe.src;
    if (src.includes('youtube') || src.includes('youtu.be')) {
      return 'youtube';
    } else if (src.includes('vimeo.com')) {
      return 'vimeo';
    }
    console.warn('Unknown video type:', src);
    return null;
  }

  private setupIframeLoadListener() {
    if (!this.iframe) return;

    // Listen for iframe load event
    this.iframe.addEventListener('load', () => {
      this.setupAPIEventListeners();
    });

    // Also listen for errors
    this.iframe.addEventListener('error', (e) => {
      console.error('Iframe load error:', e);
    });

    // Check if iframe is already loaded (in case this runs after load event)
    if (this.iframe.contentDocument && this.iframe.contentDocument.readyState === 'complete') {
      this.setupAPIEventListeners();
    }
  }

  private setupAPIEventListeners() {
    if (!this.iframe || !this.videoType) return;

    // Listen for API ready events from the video providers
    window.addEventListener('message', (event) => {
      if (event.source !== this.iframe?.contentWindow) return;

      try {
        const data = JSON.parse(event.data);
        
        if (this.videoType === 'youtube') {
          // YouTube API sends onReady event when ready
          if (data.event === 'onReady') {
            this.onVideoReady();
          }
        } else if (this.videoType === 'vimeo') {
          // Vimeo API sends ready event when ready
          if (data.event === 'ready') {
            this.onVideoReady();
          }
        }
      } catch (e) {
        // Ignore non-JSON messages
      }
    });

    // For YouTube, we need to explicitly request the ready event
    if (this.videoType === 'youtube') {
      try {
        const targetOrigin = new URL(this.iframe.src).origin;
        const message = JSON.stringify({
          event: 'listening',
          id: Math.random().toString(36).substr(2, 9),
          channel: window.location.href
        });
        this.iframe.contentWindow?.postMessage(message, targetOrigin);
      } catch (e) {
        console.error('Error setting up YouTube API listener:', e);
      }
    }
  }

  private onVideoReady() {
    this.iframeLoaded = true;
    
    // If iframe is ready and is in view, auto-play it (since autoplay is disabled)
    if (this.isInView && !this.userPaused) {
      this.controlVideo('play');
      this.isPlaying = true;
      this.updateIconVisibility();
    }
  }

  private setupIntersectionObserver(targetElement: Element) {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        this.isInView = entry.isIntersecting;
        
        if (entry.isIntersecting) {
          // Section is visible - play video only if user hasn't manually paused it and iframe is loaded
          if (!this.userPaused && this.iframeLoaded) {
            this.controlVideo('play');
            this.isPlaying = true;
            this.updateIconVisibility();
          }
        } else {
          // Section is not visible - pause video regardless of user state (only if iframe is loaded)
          if (this.iframeLoaded) {
            this.controlVideo('pause');
            this.isPlaying = false;
            this.updateIconVisibility();
          }
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '50px'
    });

    this.observer.observe(targetElement);
  }

  private togglePlayback() {
    if (this.isPlaying) {
      this.controlVideo('pause');
      this.isPlaying = false;
      this.userPaused = true;
      this.updateIconVisibility();
    } else {
      this.controlVideo('play');
      this.isPlaying = true;
      this.userPaused = false;
      this.updateIconVisibility();
    }
  }

  private controlVideo(action: 'play' | 'pause') {
    if (!this.iframe || !this.videoType || !this.iframeLoaded) {
      return;
    }
    
    try {
      // Give the iframe more time to be ready for postMessage
      if (!this.iframe.contentWindow) {
        return;
      }

      const targetOrigin = new URL(this.iframe.src).origin;
      
      let message = '';
      if (this.videoType === 'youtube') {
        const command = action === 'play' ? 'playVideo' : 'stopVideo';
        message = `{"event":"command","func":"${command}","args":""}`;
      } else if (this.videoType === 'vimeo') {
        message = `{"method":"${action}"}`;
      }
      
      this.iframe.contentWindow.postMessage(message, targetOrigin);

    } catch (e) {
      console.error('Error controlling video:', e);
    }
  }

  render() {
    const ariaLabel = this.isPlaying ? 'Pause background video' : 'Play background video';
    
    return html`
      <button 
        class="video-control" 
        aria-label="${ariaLabel}"
        @click=${this.togglePlayback}
      >
        <slot name="play-icon" @slotchange=${this.handleSlotChange}></slot>
        <slot name="pause-icon" @slotchange=${this.handleSlotChange}></slot>
      </button>
    `;
  }

  private handleSlotChange() {
    this.setupSlottedElements();
    this.updateIconVisibility();
  }

  static styles = [
    unsafeCSS(styles)
  ];
}

declare global {
  interface HTMLElementTagNameMap {
    'bg-background-video-control': BgBackgroundVideoControl;
  }
} 